import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { authAPI } from './services/api';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import PatientList from './pages/PatientList';
import Dashboard from './pages/Dashboard';
import FoodDatabase from './pages/FoodDatabase';
import ParenteralDatabase from './pages/ParenteralDatabase';
import History from './pages/History';
import PatientProfile from './pages/PatientProfile';
import AdminPanel from './pages/AdminPanel';
import ExpiredAccess from './pages/ExpiredAccess';
import DashboardLayout from './components/DashboardLayout';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Toaster } from './components/ui/toaster';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in and fetch fresh data
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Fetch fresh user data from /auth/me to get latest status
          const freshUser = await authAPI.getMe();
          setUser(freshUser);
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(freshUser));
        } catch (err) {
          console.error('Auth check failed:', err);
          // If auth check fails, use cached user or logout
          const savedUser = authAPI.getCurrentUser();
          if (savedUser) {
            setUser(savedUser);
          } else {
            authAPI.logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    authAPI.logout();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/assets/nutrimus-logo.png" 
              alt="NUTRIMUS" 
              className="h-20 w-auto object-contain animate-pulse"
            />
          </div>
          <p className="text-sm font-medium text-slate-400">Memuat NUTRIMUS...</p>
        </div>
      </div>
    );
  }

  // Check if user access has expired
  const isAccessExpired = () => {
    if (!user) return false;
    if (user.role === 'admin') return false; // Admin never expires
    if (user.status === 'expired') return true;
    
    // Check expiry date
    if (user.access_expired_date) {
      const expiry = new Date(user.access_expired_date);
      const now = new Date();
      return now > expiry;
    }
    
    return false;
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        {!user ? (
          <>
            <Route path="/" element={<Login onLogin={handleLogin} />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : isAccessExpired() ? (
          <>
            {/* Expired users can only access the expired page and logout */}
            <Route path="/access-expired" element={<ExpiredAccess />} />
            <Route path="*" element={<Navigate to="/access-expired" replace />} />
          </>
        ) : (
          <Route element={<DashboardLayout user={user} onLogout={handleLogout} />}>
            <Route path="/patients" element={<PatientList user={user} />} />
            <Route path="/patients/:patientId/calculate" element={<Dashboard user={user} onLogout={handleLogout} />} />
            <Route path="/patients/:patientId/history" element={<History />} />
            <Route path="/patients/:patientId/profile" element={<PatientProfile />} />
            <Route path="/foods" element={<FoodDatabase />} />
            <Route path="/parenteral" element={<ParenteralDatabase />} />
            <Route path="/history" element={<History />} />
            <Route path="/change-password" element={<ChangePassword />} />
            {user.role === 'admin' && (
              <Route path="/admin" element={<AdminPanel />} />
            )}
            <Route path="/" element={<Navigate to={user.role === 'admin' ? '/admin' : '/patients'} replace />} />
            <Route path="*" element={<Navigate to={user.role === 'admin' ? '/admin' : '/patients'} replace />} />
          </Route>
        )}
      </Routes>
      <Toaster />
      <PWAInstallPrompt />
    </BrowserRouter>
  );
}

export default App;
